/*
 * Sour Meter runtime. Pairs with snippets/sour-meter.liquid (the markup)
 * and snippets/sour-meter-config.liquid (the copy blob). Upgrades every
 * [data-sour-meter] on the page; the builder, the sticky bar and the cart
 * drawer all drive the same class.
 *
 * Math (from the Site Build Spec):
 *   bag_avg = quantity-weighted mean of sour_level across the bags
 *   bonus   = sum of SourUp boosts, capped (default +30)
 *   meter   = round(bag_avg + bonus), 1..(100 + cap); no bags → null → "—"
 *
 * Past 100 the needle pins at the end of the track and the "+SourUp" flare
 * shows; the number keeps counting.
 *
 * Interactive meters (.is-interactive, the builder hero) expose the pin as
 * a slider. Dragging paints a preview and emits:
 *   sour-meter:dragstart {value}
 *   sour-meter:input     {value}            (every move, consumer throttles)
 *   sour-meter:change    {value, from}      (release / keyboard / tap)
 * The consumer decides what the drag means (the builder rebuilds the box)
 * and calls setValue with the result; the meter then eases from wherever
 * it was painted to that value.
 *
 * The meter never makes a network call. Everything it needs is in the DOM.
 */
(() => {
  if (window.SourMeter) return;

  const DEFAULTS = {
    zones: [
      { min: 1, max: 44, key: 'sweet', color: 'yellow', name: 'Sweet side', line: 'Mostly sweet with a little tang.' },
      { min: 45, max: 65, key: 'sour', color: 'lime', name: 'Sour', line: "Yes, it's sour. Yes, it's perfect." },
      { min: 66, max: 80, key: 'extreme', color: 'orange', name: 'Extreme', line: "Sharp. You'll feel it." },
      { min: 81, max: 90, key: 'nobody', color: 'red', name: 'Few dare', line: 'Past what most people order.' },
      { min: 91, max: 100, key: 'rift', color: 'purple', name: 'Rift-level', line: 'As sour as it gets.' },
    ],
    empty: '—',
    boost_cap: 30,
    range_line: 'Runs from [min] to [max]. Averages [avg].',
    range_line_same: 'Every bag in your box reads [avg].',
    sourup_note: 'SourUp adds +[bonus].',
    over_label: '+SourUp',
    drag_hint: 'Drag me',
    drag_limit: "That's the ceiling. Nothing sourer in stock.",
    drag_floor: "That's as mild as it gets.",
    rebuilt: 'Rebuilt your box to [value].',
    undo_label: 'Put it back',
  };
  const ZONE_CLASSES = ['is-zone-yellow', 'is-zone-lime', 'is-zone-orange', 'is-zone-red', 'is-zone-purple'];
  const ANIM_MS = 400;
  const TRACK_START = 9;
  const TRACK_END = 95;
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  let cachedConfig = null;

  class SourMeter {
    static get config() {
      if (cachedConfig) return cachedConfig;
      let parsed = {};
      const el = document.getElementById('sour-meter-config');
      if (el) {
        try {
          parsed = JSON.parse(el.textContent) || {};
        } catch (e) {
          parsed = {};
        }
      }
      cachedConfig = Object.assign({}, DEFAULTS, parsed);
      if (!Array.isArray(cachedConfig.zones) || cachedConfig.zones.length !== 5) cachedConfig.zones = DEFAULTS.zones;
      return cachedConfig;
    }

    /** Highest value a meter can show: 100 plus the SourUp cap. */
    static get max() {
      return 100 + (Number(SourMeter.config.boost_cap) || 60);
    }

    /**
     * bags: [{sour_level, qty, title?, id?}], sourups: [{boost}] →
     * {n, avg, bonus, raw, value, min, max, min_name, max_name} or null.
     * `value` is the meter number (1..max); `avg` is the bag mean alone.
     */
    static computeDetail(bags, sourups) {
      let sum = 0;
      let n = 0;
      let min = null;
      let max = null;
      let minName = '';
      let maxName = '';
      (bags || []).forEach((b) => {
        const qty = Number(b.qty) || 0;
        const lvl = Number(b.sour_level);
        if (qty > 0 && Number.isFinite(lvl) && lvl >= 1) {
          sum += lvl * qty;
          n += qty;
          if (min == null || lvl < min) {
            min = lvl;
            minName = b.title || '';
          }
          if (max == null || lvl > max) {
            max = lvl;
            maxName = b.title || '';
          }
        }
      });
      if (n === 0) return null;
      const cap = Number(SourMeter.config.boost_cap) || 60;
      let bonus = 0;
      (sourups || []).forEach((s) => {
        bonus += Number(s.boost) || 0;
      });
      bonus = Math.min(cap, bonus);
      const avg = Math.round(sum / n);
      const raw = sum / n + bonus;
      return {
        n: n,
        avg: clamp(avg, 1, 100),
        bonus: bonus,
        raw: raw,
        value: clamp(Math.round(raw), 1, 100 + cap),
        min: Math.round(min),
        max: Math.round(max),
        min_name: minName,
        max_name: maxName,
      };
    }

    /** bags: [{sour_level, qty}], sourups: [{boost}] → 1..max or null */
    static compute(bags, sourups) {
      const d = SourMeter.computeDetail(bags, sourups);
      return d ? d.value : null;
    }

    /** The explaining sentence for a computeDetail() result. */
    static rangeText(detail) {
      if (!detail) return '';
      const cfg = SourMeter.config;
      const zone = SourMeter.zoneFor(detail.avg);
      let text = String(detail.min === detail.max ? cfg.range_line_same : cfg.range_line || '');
      text = text
        .replace(/\[min_name\]/g, detail.min_name || '')
        .replace(/\[max_name\]/g, detail.max_name || '')
        .replace(/\[min\]/g, detail.min)
        .replace(/\[max\]/g, detail.max)
        .replace(/\[avg\]/g, detail.avg)
        .replace(/\[zone\]/g, zone ? zone.name : '');
      if (detail.bonus > 0 && cfg.sourup_note) text += ' ' + String(cfg.sourup_note).replace(/\[bonus\]/g, detail.bonus);
      return text.trim();
    }

    static zoneFor(n) {
      if (n == null || !(n >= 1)) return null;
      const v = Math.min(100, Math.round(n));
      return SourMeter.config.zones.find((z) => v >= z.min && v <= z.max) || null;
    }

    static mountAll(root) {
      const scope = root || document;
      scope.querySelectorAll('[data-sour-meter]').forEach((el) => {
        if (!el.__sourMeter) new SourMeter(el);
      });
    }

    static get(el) {
      if (!el) return null;
      return el.__sourMeter || new SourMeter(el);
    }

    constructor(el) {
      this.el = el;
      el.__sourMeter = this;
      const raw = Number(el.dataset.value);
      this.value = raw >= 1 ? Math.min(SourMeter.max, raw) : null;
      this.shown = this.value;
      this.zone = SourMeter.zoneFor(this.value);
      this.barEl = el.querySelector('.sour-meter__bar');
      this.valueEl = el.querySelector('.sour-meter__value');
      this.zoneEl = el.querySelector('.sour-meter__zone');
      this.lineEl = el.querySelector('.sour-meter__line');
      this.rangeEl = el.querySelector('.sour-meter__range');
      this.dotsEl = el.querySelector('.sour-meter__dots');
      this.floatsEl = el.querySelector('.sour-meter__floats');
      this.noteEl = el.querySelector('.sour-meter__note');
      this.noteTextEl = el.querySelector('[data-meter-note]');
      this.undoBtn = el.querySelector('[data-meter-undo]');
      this.handle = el.querySelector('.sour-meter__handle');
      this.customLine = !!(this.lineEl && this.lineEl.hasAttribute('data-custom'));
      this.raf = null;
      this.noteTimer = null;
      this.dragging = false;
      this.locked = false;
      // Reflect config copy in case Liquid rendered without settings.
      if (this.zone) this.applyZone(this.zone, false);
      this.layoutDots();
      if (this.handle) {
        this.bindDrag();
        this.bindCallout();
        // The "Drag me" callout is a first-visit cue: once the shopper has
        // used a meter this session it stays away on every meter.
        try {
          if (sessionStorage.getItem(SourMeter.TOUCH_KEY)) el.classList.add('is-touched');
        } catch (err) {}
      }
    }

    /** First interaction with the pin (drag, track tap, keys): retire the callout for the session. */
    touch() {
      if (this.el.classList.contains('is-touched')) return;
      this.el.classList.add('is-touched');
      try {
        sessionStorage.setItem(SourMeter.TOUCH_KEY, '1');
      } catch (err) {}
    }

    /**
     * "Drag me" callout on the pin: shown while the pointer is away from it,
     * hidden as the pointer nears or hovers and while dragging.
     */
    bindCallout() {
      const el = this.el;
      const near = (e) => {
        if (!this.handle || this.handle.disabled) return;
        const r = this.handle.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        el.classList.toggle('is-near', Math.hypot(dx, dy) < 80);
      };
      el.addEventListener('pointermove', near);
      el.addEventListener('pointerleave', () => el.classList.remove('is-near'));
    }

    /** Set a new value. opts.float shows a "+12"-style flourish. */
    setValue(next, opts) {
      const options = opts || {};
      const target = next == null || !(next >= 1) ? null : Math.min(SourMeter.max, Math.round(next));
      const from = this.shown;
      const prevZone = this.zone;
      this.value = target;
      this.zone = SourMeter.zoneFor(target);

      if (this.raf) cancelAnimationFrame(this.raf);
      const animate = options.animate !== false && !reducedMotion() && from != null && target != null && from !== target;
      if (!animate) {
        this.paint(target);
      } else {
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / ANIM_MS);
          const cur = from + (target - from) * easeOut(t);
          this.paint(t < 1 ? cur : target);
          if (t < 1) this.raf = requestAnimationFrame(step);
          else this.raf = null;
        };
        this.raf = requestAnimationFrame(step);
      }

      if (options.float) this.float(options.float, options.floatDown);

      const prevKey = prevZone ? prevZone.key : null;
      const nextKey = this.zone ? this.zone.key : null;
      if (prevKey !== nextKey) {
        this.applyZone(this.zone, true, prevZone);
        this.el.dispatchEvent(new CustomEvent('sour-meter:zone', { bubbles: true, detail: { from: prevKey, to: nextKey, value: target } }));
      } else if (this.handle) {
        this.syncHandle();
      }
      return this;
    }

    paint(cur) {
      const shown = cur == null ? null : Math.round(cur);
      this.shown = cur == null ? null : cur;
      this.el.style.setProperty('--meter-value', cur == null ? 0 : Math.min(100, cur));
      this.el.dataset.value = shown == null ? 0 : shown;
      this.el.classList.toggle('is-empty', shown == null);
      this.el.classList.toggle('is-over', shown != null && shown > 100);
      if (this.valueEl) this.valueEl.textContent = shown == null ? SourMeter.config.empty : String(shown);
    }

    applyZone(zone, entering, prevZone) {
      ZONE_CLASSES.forEach((c) => this.el.classList.remove(c));
      if (zone) this.el.classList.add('is-zone-' + zone.color);
      this.el.dataset.zone = zone ? zone.key : '';
      if (this.zoneEl) this.zoneEl.textContent = zone ? zone.name : '';
      if (this.lineEl && !this.customLine) this.lineEl.textContent = zone ? zone.line : '';
      const label = this.el.getAttribute('aria-label');
      if (label && this.value != null && !this.handle) {
        this.el.setAttribute('aria-label', label.replace(/:.*$/, ': ' + this.value + ' out of 100' + (zone ? ', ' + zone.name : '')));
      }
      this.syncHandle();
    }

    /* ---------- dots + copy ---------- */

    /**
     * dots: [{id, level, qty, title}] → diff against the rendered dots.
     * opts.ghost paints them as a preview (dashed) instead of a change.
     */
    setDots(dots, opts) {
      if (!this.dotsEl) return;
      const options = opts || {};
      const list = (dots || []).filter((d) => Number(d.level) >= 1).map((d) => ({
        id: String(d.id != null ? d.id : d.title || d.level),
        level: clamp(Math.round(Number(d.level)), 1, 100),
        qty: Math.max(1, Number(d.qty) || 1),
        title: d.title || '',
      }));
      const seen = new Set();
      const existing = new Map();
      this.dotsEl.querySelectorAll('.sour-meter__dot:not(.is-leave)').forEach((el) => existing.set(el.dataset.dotId, el));
      const motion = !reducedMotion() && options.animate !== false;
      list.forEach((d) => {
        seen.add(d.id);
        let el = existing.get(d.id);
        if (!el) {
          el = document.createElement('span');
          el.className = 'sour-meter__dot';
          el.dataset.dotId = d.id;
          if (motion) el.classList.add('is-enter');
          this.dotsEl.appendChild(el);
          if (motion) requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-enter')));
        }
        const zone = SourMeter.zoneFor(d.level);
        ZONE_CLASSES.forEach((c) => el.classList.remove(c));
        if (zone) el.classList.add('is-zone-' + zone.color);
        el.style.setProperty('--dot-x', d.level);
        el.style.setProperty('--dot-n', d.qty);
        el.dataset.level = d.level;
        el.title = d.title ? d.title + ' · ' + d.level : String(d.level);
        el.classList.toggle('is-ghost', !!options.ghost);
      });
      existing.forEach((el, id) => {
        if (seen.has(id)) return;
        if (!motion) {
          el.remove();
          return;
        }
        el.classList.add('is-leave');
        const done = () => el.remove();
        el.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 500);
      });
      this.layoutDots();
    }

    /** Spread dots that sit within a few points of each other into lanes. */
    layoutDots() {
      if (!this.dotsEl) return;
      const dots = Array.from(this.dotsEl.querySelectorAll('.sour-meter__dot:not(.is-leave)'));
      dots.sort((a, b) => Number(a.dataset.level) - Number(b.dataset.level));
      let lane = 0;
      let prev = null;
      dots.forEach((el) => {
        const lvl = Number(el.dataset.level);
        if (prev != null && lvl - prev < 4) lane = lane === 0 ? 1 : lane === 1 ? -1 : 0;
        else lane = 0;
        el.style.setProperty('--dot-lane', lane);
        prev = lvl;
      });
    }

    setRange(text) {
      if (!this.rangeEl) return;
      const t = text == null ? '' : String(text);
      this.rangeEl.textContent = t;
      this.rangeEl.hidden = !t;
    }

    /** Fixed copy under the meter; stops applyZone from writing the zone line. */
    setLine(text) {
      if (!this.lineEl) return;
      this.customLine = text != null;
      if (text != null) {
        this.lineEl.textContent = String(text);
        this.lineEl.setAttribute('data-custom', '');
      } else {
        this.lineEl.removeAttribute('data-custom');
        this.lineEl.textContent = this.zone ? this.zone.line : '';
      }
    }

    /**
     * A status line under the meter (drag hint, limit, undo). opts.undo
     * shows the "Put it back" button; opts.ttl clears it after ms.
     */
    note(text, opts) {
      if (!this.noteEl) return;
      const options = opts || {};
      clearTimeout(this.noteTimer);
      if (!text) {
        this.noteEl.hidden = true;
        if (this.undoBtn) this.undoBtn.hidden = true;
        return;
      }
      if (this.noteTextEl) this.noteTextEl.textContent = text;
      if (this.undoBtn) this.undoBtn.hidden = !options.undo;
      this.noteEl.hidden = false;
      if (options.ttl) this.noteTimer = setTimeout(() => this.note(''), options.ttl);
    }

    float(text, down) {
      if (!this.floatsEl || reducedMotion()) return;
      const span = document.createElement('span');
      span.className = 'sour-meter__float' + (down ? ' sour-meter__float--down' : '');
      span.textContent = text;
      this.floatsEl.appendChild(span);
      span.addEventListener('animationend', () => span.remove(), { once: true });
      setTimeout(() => span.remove(), 1500);
    }

    /* ---------- interactive ---------- */

    /** Turn dragging on or off (the builder locks it for kits and monthly). */
    setInteractive(on) {
      this.locked = !on;
      this.el.classList.toggle('is-locked', !on);
      if (this.handle) this.handle.disabled = !on || this.value == null;
    }

    syncHandle() {
      if (!this.handle) return;
      const v = this.value;
      this.handle.disabled = this.locked || v == null;
      this.handle.setAttribute('aria-valuenow', v == null ? 0 : v);
      this.handle.setAttribute('aria-valuemax', SourMeter.max);
      this.handle.setAttribute('aria-valuetext', v == null ? SourMeter.config.empty : v + ' out of 100' + (this.zone ? ', ' + this.zone.name : ''));
    }

    /** Pointer x → meter value along the track; past the end keeps climbing. */
    valueAt(clientX) {
      const rect = this.barEl.getBoundingClientRect();
      if (!rect.width) return this.value || 1;
      const pct = ((clientX - rect.left) / rect.width) * 100;
      const v = ((pct - TRACK_START) / (TRACK_END - TRACK_START)) * 100;
      return clamp(Math.round(v), 1, SourMeter.max);
    }

    bindDrag() {
      const bar = this.barEl;
      const handle = this.handle;
      if (!bar || !handle) return;
      let pointerId = null;
      let startX = 0;
      let moved = false;
      let from = null;

      const begin = (e) => {
        if (this.locked || this.value == null || e.button > 0) return;
        this.touch();
        pointerId = e.pointerId;
        startX = e.clientX;
        moved = false;
        from = this.value;
        this.dragging = true;
        this.el.classList.add('is-dragging');
        try {
          handle.setPointerCapture(pointerId);
        } catch (err) {}
        if (this.raf) cancelAnimationFrame(this.raf);
        this.el.dispatchEvent(new CustomEvent('sour-meter:dragstart', { bubbles: true, detail: { value: from } }));
        preview(e);
        e.preventDefault();
      };
      const preview = (e) => {
        const v = this.valueAt(e.clientX);
        if (Math.abs(e.clientX - startX) > 3) moved = true;
        this.paint(v);
        this.el.dispatchEvent(new CustomEvent('sour-meter:input', { bubbles: true, detail: { value: v, from: from } }));
      };
      const end = (e, cancelled) => {
        if (pointerId == null) return;
        try {
          handle.releasePointerCapture(pointerId);
        } catch (err) {}
        pointerId = null;
        this.dragging = false;
        this.el.classList.remove('is-dragging');
        if (cancelled) {
          this.setValue(from, { animate: true });
          this.el.dispatchEvent(new CustomEvent('sour-meter:dragcancel', { bubbles: true, detail: { value: from } }));
          return;
        }
        const v = this.valueAt(e.clientX);
        this.change(v, from);
      };

      handle.addEventListener('pointerdown', begin);
      bar.addEventListener('pointerdown', (e) => {
        // Tap or press anywhere on the track: jump the pin there.
        if (e.target === handle || this.locked || this.value == null) return;
        begin(e);
      });
      const onMove = (e) => {
        if (pointerId == null || e.pointerId !== pointerId) return;
        preview(e);
      };
      const onUp = (e) => {
        if (pointerId == null || e.pointerId !== pointerId) return;
        end(e, false);
      };
      const onCancel = (e) => {
        if (pointerId == null || e.pointerId !== pointerId) return;
        end(e, true);
      };
      // Window listeners see the captured events too, and keep working
      // where capture is refused (older WebKit).
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);

      handle.addEventListener('keydown', (e) => {
        if (this.locked || this.value == null) return;
        let next = null;
        const big = e.shiftKey ? 10 : 1;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowUp':
            next = this.value + big;
            break;
          case 'ArrowLeft':
          case 'ArrowDown':
            next = this.value - big;
            break;
          case 'PageUp':
            next = this.value + 10;
            break;
          case 'PageDown':
            next = this.value - 10;
            break;
          case 'Home':
            next = 1;
            break;
          case 'End':
            next = SourMeter.max;
            break;
          default:
            return;
        }
        e.preventDefault();
        this.change(clamp(next, 1, SourMeter.max), this.value);
      });

      if (this.undoBtn) {
        this.undoBtn.addEventListener('click', () => {
          this.el.dispatchEvent(new CustomEvent('sour-meter:undo', { bubbles: true }));
        });
      }
    }

    /**
     * Commit a requested value. Consumers listen for sour-meter:change and
     * call setValue with what they could actually reach; if nobody does,
     * the meter eases back to where it was.
     */
    change(value, from) {
      this.touch();
      const before = this.value;
      const ev = new CustomEvent('sour-meter:change', { bubbles: true, cancelable: true, detail: { value: value, from: from == null ? before : from } });
      this.el.dispatchEvent(ev);
      if (!ev.defaultPrevented) this.setValue(before, { animate: true });
    }
  }

  SourMeter.TOUCH_KEY = 'sb:meter:touched';
  window.SourMeter = SourMeter;
  const boot = () => SourMeter.mountAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => SourMeter.mountAll(e.target));
})();
